import { ViewListIcon } from '@heroicons/react/solid';
import { Button, Header, Hyperlink } from '@see-ui/core';
import { UserRound } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import flLogo from '@assets/images/brands/FL_brand_logo.jpeg';
import gaLogo from '@assets/images/brands/GA_brand_logo.jpeg';
import ksLogo from '@assets/images/brands/KS_logo.png';
import ncLogo from '@assets/images/brands/NC_logo.png';
import topLogoAbc from '@assets/images/brands/top-logo-abc.svg';
import ohLogo from '@assets/images/brands/OH_abs_r_h_rgb_c.png';
import vaLogo from '@assets/images/brands/va_logo.svg';
import MobileMenu from '@components/MobileMenu/MobileMenu';
import NavLink from '@components/NavLink/NavLink';
import { ROUTES } from '@config/routes-config';
import { logout as logoutapi } from '@services/api/login-service';
import { useGlobalStore } from '@services/store/global-store';
import { useAuth } from '../../context/AuthContext';
import { getContent } from '@services/api/strapi-service';
import * as localContent from './AppHeader.content.json';
import { useConfig } from '@components/ConfigProvider';

const AppHeader: React.FC = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState<any>();
  const { preference } = useGlobalStore();
  const { logout } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showMobileSlider, setShowMobileSlider] = useState(false);
  const { updateBannerContent } = useGlobalStore((state) => state);
  const cceName = useGlobalStore.getState().userDetails.userInformation.cceName;
  const config = useConfig();

  const handleChangePasswordClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    navigate(ROUTES.CHANGEACCESSCODE);
  };

  const userState = useGlobalStore(
    (state) => state.userDetails.userInformation.state
  );

  let bannerContent = useGlobalStore((state) => state.bannerContent);
  const userSubType = useGlobalStore(
    (state) => state.userDetails.userInformation.userSubType
  );

  const navigateToDashboard = () => {
    if (
      useGlobalStore.getState().userDetails.userInformation?.userRole ===
      'member'
    ) {
      return;
    } else {
      navigate(ROUTES.DASHBOARD);
    }
  };

  const showManageUser = true;

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleDownload = () => {
    const docUrl = content.MFALink;
    const link = document.createElement('a');
    link.href = docUrl;
    link.download = 'SOP_for_Care360_MFA.docx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        if (bannerContent) {
          setContent(bannerContent);
        } else {
          const data = await getContent('/dashboard-banner', userState);
          setContent(data);
          updateBannerContent(data);
        }
      } catch (error) {
        console.error('Error fetching content:', error);
        setContent(localContent['DEFAULT']);
      }
    };
    fetchContent();
  }, []);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setDropdownOpen(false);
    }
  };
  const onChangeShowMobileSlider = (e) => {
    setShowMobileSlider(true);
  };

  const handleMobileChildChange = (e) => {
    setShowMobileSlider(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const openStateUrls = () => {
    const externalURLs = {
      PROVIDER_FINDER_URL: config?.VITE_PROVIDER_FINDER_URL,
      PROVIDER_FINDER_URL_GA: config?.VITE_PROVIDER_FINDER_URL_GA
    };
    const url =
      userState === 'GA'
        ? externalURLs.PROVIDER_FINDER_URL_GA
        : externalURLs.PROVIDER_FINDER_URL;

    window.open(url, '_blank');
  };

  const removeLivePersonScript = () => {
    const script = document.querySelector(
      'script[src*="lptag.liveperson.net"]'
    );
    if (script) {
      document.head.removeChild(script);
      console.log('LivePerson script removed from the DOM.');
    }
    // window.location.reload();
  };

  async function handleLogout() {
    try {
      await logoutapi();
      // removeLivePersonScript();
      sessionStorage.clear();
    } catch (error) {
      console.error('Logout failed:', error);
    }
    removeLivePersonScript();
    logout();
    // if (userSubType === 'SSO' && cceName !== 'CCWW') {
    //   navigate(ROUTES.SSO_LOGOUT);
    // } else {
    //   logout();
    //   navigate(ROUTES.LOGIN);
    // }
  }

  return (
    <>
      <Header>
        <div>
          <div className="print-only">
            <div className="flex sae-logos">
              {!['GA', 'FL', 'VA', 'NC', 'KS', 'OH'].includes(
                userState as string
              ) && (
                <img
                  src={topLogoAbc}
                  alt="Anthem"
                  onClick={navigateToDashboard}
                  style={{ cursor: 'pointer' }}
                />
              )}
              {userState === 'GA' && (
                <img
                  src={gaLogo}
                  alt="GeorgiaCARE360"
                  onClick={navigateToDashboard}
                  style={{ cursor: 'pointer' }}
                />
              )}
              {userState === 'OH' && (
                <img
                  src={ohLogo}
                  alt="OhioCare360"
                  onClick={navigateToDashboard}
                  style={{ cursor: 'pointer' }}
                />
              )}
              {userState === 'FL' && (
                <img
                  src={flLogo}
                  alt="FloridaCARE360"
                  onClick={navigateToDashboard}
                  style={{ cursor: 'pointer' }}
                />
              )}
              {userState === 'NC' && (
                <img
                  src={ncLogo}
                  alt="NCCARE360"
                  onClick={navigateToDashboard}
                  style={{ cursor: 'pointer' }}
                />
              )}
              {userState === 'KS' && (
                <img
                  src={ksLogo}
                  alt="KSCARE360"
                  onClick={navigateToDashboard}
                  style={{ cursor: 'pointer' }}
                />
              )}
              {userState === 'VA' && (
                <img
                  src={vaLogo}
                  alt="VACARE360"
                  onClick={navigateToDashboard}
                  style={{ cursor: 'pointer' }}
                />
              )}
            </div>
          </div>

          <div className="no-print grid grid-cols-2">
            <div className="sae-logos">
              {!['GA', 'FL', 'VA', 'NC', 'KS', 'OH'].includes(
                userState as string
              ) && (
                <img
                  src={topLogoAbc}
                  alt="Anthem"
                  onClick={navigateToDashboard}
                  style={{ cursor: 'pointer' }}
                />
              )}
              {userState === 'GA' && (
                <img
                  src={gaLogo}
                  alt="GeorgiaCARE360"
                  onClick={navigateToDashboard}
                  style={{ cursor: 'pointer' }}
                />
              )}
              {userState === 'OH' && (
                <img
                  src={ohLogo}
                  alt="OhioCare360"
                  onClick={navigateToDashboard}
                  style={{ cursor: 'pointer' }}
                />
              )}
              {userState === 'FL' && (
                <img
                  src={flLogo}
                  alt="FloridaCARE360"
                  onClick={navigateToDashboard}
                  style={{ cursor: 'pointer' }}
                />
              )}
              {userState === 'NC' && (
                <img
                  src={ncLogo}
                  alt="NCCARE360"
                  onClick={navigateToDashboard}
                  style={{ cursor: 'pointer' }}
                />
              )}
              {userState === 'KS' && (
                <img
                  src={ksLogo}
                  alt="KSCARE360"
                  onClick={navigateToDashboard}
                  style={{ cursor: 'pointer' }}
                  className="ks_logo"
                />
              )}
              {userState === 'VA' && (
                <img
                  src={vaLogo}
                  alt="VACARE360"
                  onClick={navigateToDashboard}
                  style={{ cursor: 'pointer' }}
                  className="ks_logo"
                />
              )}
            </div>
            <div
              className="cursor-pointer flex justify-end"
              style={{ color: '#0079C2' }}
              ref={dropdownRef}
            >
              <button
                type="button"
                onClick={toggleDropdown}
                aria-haspopup="menu"
                aria-expanded={dropdownOpen}
                aria-controls="profile-menu"
                className="mr-8 flex flex-col items-center pt-4 hover:bg-gray-200 rounded-full p-2"
              >
                <UserRound /> <span className="text-sm">Profile</span>
              </button>
              <div className="mobile-menu">
                <a onClick={(e) => onChangeShowMobileSlider(e)}>
                  {' '}
                  <ViewListIcon className="w-6 h-10" />
                </a>
              </div>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded shadow-lg">
                  {userSubType !== 'SSO' && (
                    <Hyperlink
                      className="block px-4 py-2 text-blue-600 text-sm underline"
                      children="Change Password"
                      onClick={handleChangePasswordClick}
                    />
                  )}
                  <Hyperlink
                    className="block px-4 py-2 text-blue-600 text-sm underline"
                    children="Log Out"
                    onClick={(e) => {
                      handleLogout();
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="horiz-nav-links">
            <NavLink />
          </div>
        </div>
      </Header>
      {preference?.dashboard?.banner && (
        <>
          <div className="centered-message">
            {content?.TutorialsText && (
              <div>
                {content.TutorialsText}
                <Button
                  variant="link"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(ROUTES.TUTORIALS);
                  }}
                >
                  visit our tutorials
                </Button>
              </div>
            )}

            {content?.MFAText && (
              <div>
                {content.MFAText}
                {content.HyperLink && content.MFALink && (
                  <Button variant="link" onClick={handleDownload}>
                    {content.HyperLink}
                  </Button>
                )}
              </div>
            )}
          </div>
        </>
      )}
      <MobileMenu
        showMobileSlider={showMobileSlider}
        onClose={handleMobileChildChange}
      ></MobileMenu>
    </>
  );
};

export default AppHeader;
